/**
 * DeleteStageDialogView — the confirmation surface for deleting a pipeline stage
 * (SE-5488, AC2 + AC3).
 *
 * Pure presentation: it maps an already-resolved {@link DialogCopy} onto the
 * shared `ConfirmDialog`, so every "are you sure?" in the app keeps
 * one look and one testID scheme. Choosing that copy from the deletion plan is
 * `DeleteStageDialogContainer`'s job (SE-8019) — the four plan variants and
 * their gating live there, and this file renders whichever one it is handed.
 * @module features/player-kanban/components/molecules/DeleteStageDialog/DeleteStageDialogView
 */

import { AlertTriangle } from "lucide-react-native";
import { memo } from "react";

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

import ConfirmDialog from "@/components/molecules/ConfirmDialog";

/** Props for {@link DeleteStageDialogView}. */
export interface DeleteStageDialogViewProps {
  /** Whether the dialog is visible. */
  readonly isOpen: boolean;
  /** The resolved copy + gating for this deletion, from the Container. */
  readonly copy: DialogCopy;
  /** Whether the confirmed delete is in flight. */
  readonly isConfirming: boolean;
  /** Confirm handler — only reachable when the plan permits deletion. */
  readonly onConfirm: () => void;
  /** Cancel / dismiss handler. */
  readonly onClose: () => void;
}

/** Resolved copy + gating for one plan variant. */
export interface DialogCopy {
  readonly title: string;
  readonly body: readonly string[];
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly tone: "danger" | "warning";
  readonly isConfirmDisabled: boolean;
}

/**
 * Render the stage-deletion confirmation.
 * @param props - {@link DeleteStageDialogViewProps}.
 * @param props.isOpen - Whether the dialog is visible.
 * @param props.copy - The resolved copy + gating.
 * @param props.isConfirming - Whether the confirmed delete is in flight.
 * @param props.onConfirm - Confirm handler.
 * @param props.onClose - Cancel / dismiss handler.
 * @returns The confirm dialog.
 */
const DeleteStageDialogView = ({
  isOpen,
  copy,
  isConfirming,
  onConfirm,
  onClose,
}: DeleteStageDialogViewProps) => (
  <ConfirmDialog
    isOpen={isOpen}
    onClose={onClose}
    onConfirm={onConfirm}
    title={copy.title}
    body={copy.body}
    icon={AlertTriangle}
    tone={copy.tone}
    confirmLabel={copy.confirmLabel}
    cancelLabel={copy.cancelLabel}
    isConfirming={isConfirming}
    isConfirmDisabled={copy.isConfirmDisabled}
    testID={TEST_ID_PLAYER_KANBAN.STAGE_EDITOR_DELETE_DIALOG}
  />
);

DeleteStageDialogView.displayName = "DeleteStageDialogView";

export default memo(DeleteStageDialogView);
